import { Link } from "react-router-dom";
import { BrandImage } from "@/lib/brand";
import { BrandFilm } from "@/components/BrandFilm";
import { LabPricingSlider } from "@/components/LabPricingSlider";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import {
    ArrowUpRight,
    ArrowRight,
    ShieldCheck,
    Sparkles,
    Timer,
    Layers,
    CircuitBoard,
    Activity,
    Cpu,
    Beaker,
    CheckCircle2,
} from "lucide-react";

const HERO_IMG =
    "https://images.unsplash.com/photo-1771442873035-474765b40ac6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxkZW50YWwlMjBsYWIlMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc3ODQwOTM2OXww&ixlib=rb-4.1.0&q=85";

const TRUST = [
    "3SHAPE", "MEDIT", "ITERO", "DEXIS", "SHINING 3D", "EXOCAD", "PRIMESCAN",
    "TRIOS", "CARESTREAM",
];

const OFFERINGS = [
    {
        slug: "permanent-crown",
        title: "Permanent Crown",
        meta: "Posterior · Anterior",
        body: "Full-contour Nano Ceramic Hybrid restoration, milled-grade fit.",
        accent: "from-cyan-100 to-white",
        icon: Layers,
        img: "https://images.unsplash.com/photo-1606811971618-4486d14f3f99?auto=format&fit=crop&w=900&q=80",
    },
    {
        slug: "same-day-crown",
        title: "Same-Day Crown",
        meta: "Single visit",
        body: "Scan in the morning, dispatch by evening. Same-day across Doha.",
        accent: "from-teal-50 to-white",
        icon: Timer,
        img: "https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=900&q=80",
    },
    {
        slug: "veneers",
        title: "Veneers",
        meta: "Anterior aesthetics",
        body: "0.3–0.5 mm Nano Hybrid laminates with translucency calibration.",
        accent: "from-slate-100 to-white",
        icon: Sparkles,
        img: "https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&w=900&q=80",
    },
    {
        slug: "inlay-onlay",
        title: "Inlays / Onlays",
        meta: "Conservative restorations",
        body: "Tooth-conserving alternatives to full-coverage crowns.",
        accent: "from-cyan-50 to-white",
        icon: CircuitBoard,
        img: "https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&w=900&q=80",
    },
];

const ADVANTAGES = [
    {
        title: "Metal & Zirconia Free",
        body: "A purely polymer-ceramic matrix — no allergenic metal substructure, no abrasive zirconia wear on opposing dentition.",
        icon: ShieldCheck,
        kpi: "0% metal",
    },
    {
        title: "Unmatched Precision",
        body: "Sub-25-micron print resolution with closed-loop calibration. Margins seat without adjustment, chairside.",
        icon: Cpu,
        kpi: "<25 µm",
    },
    {
        title: "Rapid Turnaround",
        body: "Dispatch from 3 working hours for single units. Same-day for cases received before 11 AM.",
        icon: Activity,
        kpi: "6–9 hrs",
    },
];

export default function Home() {
    return (
        <div data-testid="home-page" className="bg-white">
            {/* HERO */}
            <section className="relative overflow-hidden">
                <div className="absolute -top-40 -right-20 w-[640px] h-[640px] tr-glow bg-cyan-300/40" aria-hidden />
                <div className="absolute top-40 -left-32 w-[420px] h-[420px] tr-glow bg-teal-200/40" aria-hidden />

                {/* Ambient tech pattern behind the hero — flowing grid + circuit lines + drifting particles */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden>
                    <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice" viewBox="0 0 1280 720" xmlns="http://www.w3.org/2000/svg">
                        <defs>
                            <pattern id="tech-grid" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
                                <path d="M 60 0 L 0 0 0 60" fill="none" stroke="rgba(8,145,178,0.07)" strokeWidth="1" />
                            </pattern>
                            <linearGradient id="tech-fade" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="white" stopOpacity="0" />
                                <stop offset="50%" stopColor="white" stopOpacity="1" />
                                <stop offset="100%" stopColor="white" stopOpacity="0" />
                            </linearGradient>
                            <mask id="tech-mask">
                                <rect width="1280" height="720" fill="url(#tech-fade)" />
                            </mask>
                            <linearGradient id="tech-line" x1="0" x2="1" y1="0" y2="0">
                                <stop offset="0%" stopColor="rgba(34,211,238,0)" />
                                <stop offset="50%" stopColor="rgba(34,211,238,0.5)" />
                                <stop offset="100%" stopColor="rgba(34,211,238,0)" />
                            </linearGradient>
                        </defs>

                        {/* base grid */}
                        <rect width="1280" height="720" fill="url(#tech-grid)" mask="url(#tech-mask)" />

                        {/* flowing horizontal scan-lines */}
                        <g mask="url(#tech-mask)" className="hero-scan-group">
                            <line x1="-200" y1="180" x2="600" y2="180" stroke="url(#tech-line)" strokeWidth="1" className="hero-scan hero-scan-1" />
                            <line x1="-200" y1="360" x2="600" y2="360" stroke="url(#tech-line)" strokeWidth="1" className="hero-scan hero-scan-2" />
                            <line x1="-200" y1="520" x2="600" y2="520" stroke="url(#tech-line)" strokeWidth="1" className="hero-scan hero-scan-3" />
                        </g>

                        {/* connection nodes — circuit-style */}
                        <g className="hero-nodes" opacity="0.55">
                            {[
                                [120, 220], [260, 140], [200, 320], [80, 460],
                                [340, 480], [180, 580], [420, 280], [60, 360],
                                [380, 160], [240, 220],
                            ].map(([cx, cy], i) => (
                                <g key={`n-${i}`} style={{ animationDelay: `${(i * 0.4) % 5}s` }} className="hero-node">
                                    <circle cx={cx} cy={cy} r="2.5" fill="rgba(34,211,238,0.55)" />
                                    <circle cx={cx} cy={cy} r="9" fill="none" stroke="rgba(34,211,238,0.25)" strokeWidth="0.6" />
                                </g>
                            ))}
                        </g>

                        {/* connecting circuit lines (static, subtle) */}
                        <g stroke="rgba(34,211,238,0.18)" strokeWidth="0.7" fill="none">
                            <path d="M 120 220 L 200 320 L 80 460" />
                            <path d="M 260 140 L 380 160 L 420 280" />
                            <path d="M 340 480 L 180 580" />
                            <path d="M 240 220 L 120 220" />
                            <path d="M 420 280 L 340 480" />
                        </g>

                        {/* drifting binary glyphs — barely visible */}
                        <g fill="rgba(8,145,178,0.18)" fontFamily="monospace" fontSize="9">
                            <text x="40" y="120" className="hero-drift hero-drift-1">01001110</text>
                            <text x="320" y="80" className="hero-drift hero-drift-2">N-Ce·H₃</text>
                            <text x="60" y="640" className="hero-drift hero-drift-3">µ ← 22</text>
                            <text x="380" y="600" className="hero-drift hero-drift-4">λ 405nm</text>
                        </g>
                    </svg>
                </div>

                <div className="relative max-w-[1280px] mx-auto px-5 lg:px-8 pt-16 lg:pt-24 pb-16 lg:pb-28 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7 tr-fade-up">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 bg-white/70 backdrop-blur text-[11.5px] font-mono-tr tracking-[0.18em] uppercase text-slate-700">
                            <span className="h-1.5 w-1.5 rounded-full bg-cyan-500 tr-pulse-dot" />
                            Now printing in Qatar
                        </div>
                        <h1 className="mt-7 font-display font-light text-[44px] sm:text-[60px] lg:text-[78px] leading-[0.96] tracking-[-0.02em] text-slate-900">
                            Next-Generation<br />
                            <span className="italic font-extralight text-slate-700">3D Printed</span><br />
                            Dentistry.
                        </h1>
                        <p className="mt-7 max-w-xl text-[16.5px] leading-[1.7] text-slate-600">
                            Precision-engineered Nano Hybrid Ceramic crowns, veneers and inlays.
                            Same-day dispatch. <span className="text-slate-900 font-semibold">10-year clinical warranty.</span>
                        </p>
                        <div className="mt-9 flex flex-wrap items-center gap-3">
                            <Link to="/workflow#send-case" data-testid="hero-send-case" className="tr-btn">
                                Send a Case <ArrowUpRight size={16} />
                            </Link>
                            <Link to="/technology" data-testid="hero-explore-materials" className="tr-btn-ghost">
                                Explore Materials <ArrowRight size={16} />
                            </Link>
                        </div>

                        <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
                            {[
                                { k: "10Y", v: "Clinical warranty" },
                                { k: "<25µ", v: "Margin precision" },
                                { k: "from 3h", v: "Avg. dispatch" },
                            ].map((s) => (
                                <div key={s.k}>
                                    <div className="font-display text-3xl text-slate-900 tracking-tight">{s.k}</div>
                                    <div className="text-[11px] mt-1 text-slate-500 font-mono-tr tracking-[0.16em] uppercase">{s.v}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-5 relative">
                        <div className="absolute -inset-6 rounded-[28px] bg-gradient-to-br from-cyan-200/60 via-white to-teal-100/60 blur-2xl" aria-hidden />
                        <div className="relative rounded-[24px] overflow-hidden border border-slate-200 shadow-[0_30px_80px_-30px_rgba(8,47,73,0.35)] h-[440px] lg:h-[560px]">
                            <BrandFilm />
                        </div>
                    </div>
                </div>
            </section>

            {/* CASE STUDY HIGHLIGHT — Patient X */}
            <section className="bg-[#0B1120] text-white relative overflow-hidden tr-grain" data-testid="home-case-study">
                <div className="absolute -top-24 right-0 w-[380px] h-[380px] tr-glow bg-cyan-500/20" aria-hidden />
                <div className="relative max-w-[1280px] mx-auto px-5 lg:px-8 py-20 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                    <div className="lg:col-span-7">
                        <div className="font-mono-tr text-[10.5px] tracking-[0.28em] uppercase text-cyan-300/80 mb-4">· Case study · Oncology</div>
                        <h3 className="font-display font-light text-3xl sm:text-4xl tracking-tight leading-[1.12]">
                            Two failed metal onlays. A cancer treatment on hold.<br />
                            <span className="text-cyan-300">How our double-bonded nano-hybrid ceramic saved Patient X&rsquo;s timeline.</span>
                        </h3>
                        <p className="mt-4 text-[14.5px] text-slate-400 leading-[1.75] max-w-2xl">
                            While the market still bonds molecules once, Toothrocket&rsquo;s nanotech bonds them twice — the
                            marginal precision that got an immunocompromised patient cleared for cancer therapy on time,
                            after conventional laboratory work failed twice.
                        </p>
                        <Link to="/case-studies/patient-x" data-testid="home-case-study-link"
                            className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-cyan-400 text-[#0B1120] text-[14px] font-medium hover:bg-cyan-300 transition">
                            Read the full case study <ArrowRight size={15} />
                        </Link>
                    </div>
                    <div className="lg:col-span-5">
                        <BeforeAfterSlider
                            before="/case-study/metal-gap-highlight.jpg"
                            after="/case-study/high-points-intraoral.jpg"
                            beforeLabel="Before · failed metal"
                            afterLabel="After · ceramic seated"
                            className="h-[380px] lg:h-[440px] w-full"
                        />
                        <p className="mt-3 text-[12px] text-slate-500 leading-snug" data-testid="home-case-study-slider-caption">
                            Drag the handle — the failed metal onlay vs the nano-hybrid ceramic seated. Real, unretouched
                            clinical photography, shared with patient consent and identity redacted.
                        </p>
                    </div>
                </div>
            </section>

            {/* TRUST BAR */}
            <section className="border-y border-slate-200 bg-slate-50/60 overflow-hidden" data-testid="trust-bar">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-7 flex items-center gap-8">
                    <div className="hidden md:block tr-overline whitespace-nowrap">Integrates with</div>
                    <div className="relative flex-1 overflow-hidden">
                        <div className="flex items-center gap-12 tr-marquee whitespace-nowrap w-max">
                            {[...TRUST, ...TRUST].map((t, i) => (
                                <div key={`trust-${t}-${i}`} className="font-display text-slate-400 text-[15px] tracking-[0.18em] uppercase font-medium hover:text-slate-700 transition">
                                    {t}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CORE OFFERINGS */}
            <section className="max-w-[1280px] mx-auto px-5 lg:px-8 py-24 lg:py-32" data-testid="offerings-section">
                <div className="grid lg:grid-cols-12 gap-10 mb-14">
                    <div className="lg:col-span-5">
                        <div className="tr-overline mb-4">· Core offerings</div>
                        <h2 className="font-display font-light text-4xl sm:text-5xl tracking-tight leading-[1.05] text-slate-900">
                            Four restorations,<br />
                            <span className="italic">one printable matrix.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 self-end">
                        <p className="text-[15.5px] text-slate-600 leading-[1.75] max-w-lg">
                            Every Toothrocket case is fabricated from the same Nano Ceramic Hybrid
                            Resin — calibrated for the indication, milled to fit, and shade-mapped to
                            the natural dentition.
                        </p>
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-12 gap-4 lg:gap-5">
                    {OFFERINGS.map((o, idx) => {
                        const Icon = o.icon;
                        // Bento layout: 0 is the large "hero" tile; 1/2 are half-width; 3 is wide.
                        const SPANS = ["lg:col-span-7 lg:row-span-2", "lg:col-span-5", "lg:col-span-5", "lg:col-span-7"];
                        const span = SPANS[idx] || "lg:col-span-7";
                        const tall = idx === 0 ? "min-h-[360px]" : "min-h-[200px]";
                        return (
                            <Link
                                to="/for-clinics#restoratives"
                                key={o.slug}
                                data-testid={`offering-${o.slug}`}
                                className={`tr-card relative ${span} ${tall} overflow-hidden flex flex-col justify-between bg-gradient-to-br ${o.accent} group`}
                            >
                                {/* image holder — Admin can override via Branding tab slug `home_<slug>` */}
                                <BrandImage
                                    slug={`home_${o.slug}`}
                                    fallback={o.img}
                                    alt={o.title}
                                    className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-95 group-hover:scale-[1.03] transition-all duration-700"
                                />
                                {/* readable text scrim — solid bottom band so the headline pops cleanly off the photo */}
                                <div
                                    className="absolute inset-x-0 top-0 h-1/2"
                                    aria-hidden
                                    style={{
                                        background: "linear-gradient(to bottom, rgba(255,255,255,0.65), rgba(255,255,255,0))",
                                    }}
                                />
                                <div
                                    className="absolute inset-x-0 bottom-0 h-[58%]"
                                    aria-hidden
                                    style={{
                                        background: "linear-gradient(to top, rgba(255,255,255,1) 25%, rgba(255,255,255,0.92) 55%, rgba(255,255,255,0) 100%)",
                                    }}
                                />
                                {/* Dark-mode variants */}
                                <div
                                    className="absolute inset-x-0 top-0 h-1/2 hidden dark:block"
                                    aria-hidden
                                    style={{
                                        background: "linear-gradient(to bottom, rgba(11,17,32,0.55), rgba(11,17,32,0))",
                                    }}
                                />
                                <div
                                    className="absolute inset-x-0 bottom-0 h-[58%] hidden dark:block"
                                    aria-hidden
                                    style={{
                                        background: "linear-gradient(to top, rgba(11,17,32,1) 25%, rgba(11,17,32,0.92) 55%, rgba(11,17,32,0) 100%)",
                                    }}
                                />

                                <div className="relative p-7 flex items-start justify-between">
                                    <div className="h-10 w-10 rounded-xl bg-white/95 backdrop-blur border border-slate-200 flex items-center justify-center text-slate-700 group-hover:text-cyan-600 group-hover:border-cyan-200 transition shadow-sm">
                                        <Icon size={18} />
                                    </div>
                                    <div className="font-mono-tr text-[10px] tracking-[0.22em] uppercase text-slate-600 bg-white/85 backdrop-blur px-2 py-1 rounded-md border border-white">
                                        {o.meta}
                                    </div>
                                </div>
                                <div className="relative p-7 pt-0">
                                    <div className="font-display text-2xl lg:text-[28px] tracking-tight text-slate-900 mb-2 drop-shadow-sm">
                                        {o.title}
                                    </div>
                                    <p className="text-[14.5px] text-slate-700 leading-relaxed max-w-md">{o.body}</p>
                                    <div className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-900 group-hover:text-cyan-700 transition">
                                        See indications <ArrowRight size={14} />
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </section>

            {/* CLINICAL ADVANTAGE — DARK */}
            <section className="relative tr-grain bg-[#0B1120] text-white overflow-hidden" data-testid="advantage-section">
                <div className="absolute -top-40 right-1/3 w-[500px] h-[500px] tr-glow bg-cyan-500/30" />
                <div className="absolute bottom-0 -left-32 w-[420px] h-[420px] tr-glow bg-teal-500/20" />
                <div className="relative max-w-[1280px] mx-auto px-5 lg:px-8 py-24 lg:py-32">
                    <div className="grid lg:grid-cols-12 gap-10 mb-16">
                        <div className="lg:col-span-7">
                            <div className="tr-overline text-cyan-400 mb-4">· The clinical advantage</div>
                            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
                                Why prosthodontists<br />
                                are switching <span className="italic text-cyan-300/90 font-extralight">in 2026.</span>
                            </h2>
                        </div>
                        <div className="lg:col-span-4 lg:col-start-9 self-end">
                            <p className="text-white/65 leading-[1.75] text-[15px]">
                                A measured, clinical case for the Nano Ceramic Hybrid Resin matrix —
                                tested on 12,000+ live restorations across 480 partner clinics.
                            </p>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
                        {ADVANTAGES.map((a) => {
                            const Icon = a.icon;
                            return (
                                <div key={a.title} className="bg-[#0B1120] p-8 lg:p-10 flex flex-col gap-5 hover:bg-[#0d1426] transition group">
                                    <div className="flex items-center justify-between">
                                        <div className="h-11 w-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-cyan-300 group-hover:border-cyan-400/40 transition">
                                            <Icon size={20} />
                                        </div>
                                        <div className="font-display text-3xl text-cyan-300 tracking-tight">{a.kpi}</div>
                                    </div>
                                    <div>
                                        <div className="font-display text-2xl tracking-tight">{a.title}</div>
                                        <p className="mt-3 text-white/65 leading-[1.7] text-[14.5px]">{a.body}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* WORKFLOW PREVIEW */}
            {/* WARRANTY STRIP */}
            <section className="border-t border-slate-200 bg-slate-50">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-20 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <div className="tr-overline mb-3">· The 10-year promise</div>
                        <h3 className="font-display font-light text-3xl sm:text-4xl tracking-tight leading-[1.1] text-slate-900">
                            We replace, repair or refund any restoration that fails clinically — for a decade.
                        </h3>
                        <ul className="mt-6 space-y-2.5">
                            {[
                                "Covers fractures, debonding, and chipping under normal occlusal load.",
                                "ToothPassport™ — a live QR on every case. Scan it anytime to validate the warranty online: no PVC cards to store, nothing to lose. The warranty stays active and alive online, forever.",
                                "Valid anywhere in the world — if your patient moves country, any dentist can scan the ToothPassport and request a replacement. We manufacture at our nearest laboratory and deliver free of charge.",
                                "Direct replacement printed within 12 hours of warranty claim.",
                            ].map((p) => (
                                <li key={p} className="flex items-start gap-3 text-[15px] text-slate-700">
                                    <CheckCircle2 size={18} className="text-cyan-600 mt-0.5 shrink-0" />
                                    {p}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="lg:col-span-4 lg:col-start-9">
                        <div className="rounded-2xl border border-slate-200 bg-white p-7" data-testid="home-toothpassport-card">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <div className="font-mono-tr text-[10.5px] tracking-[0.24em] uppercase text-cyan-600">ToothPassport™</div>
                                    <div className="mt-1.5 font-display text-[19px] text-slate-900 tracking-tight leading-snug">Scan me —<br />live demo passport</div>
                                </div>
                                <div className="font-display text-5xl text-slate-900 tracking-tighter leading-none">10<span className="text-cyan-500">.</span></div>
                            </div>
                            <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-3 flex items-center gap-4">
                                <img
                                    src={`${process.env.REACT_APP_BACKEND_URL}/api/warranty/qr/TR-DEMO-2026.png`}
                                    alt="Scan to open the demo ToothPassport"
                                    data-testid="home-toothpassport-qr"
                                    className="w-28 h-28 rounded-lg bg-white p-1.5"
                                />
                                <div className="text-[12px] text-slate-600 leading-relaxed">
                                    Point your phone camera here — the demo warranty opens instantly, verified live.
                                    Every restoration we ship carries its own.
                                </div>
                            </div>
                            <div className="mt-5 pt-5 border-t border-slate-100 flex items-center justify-between">
                                <Link to="/warranty/TR-DEMO-2026" data-testid="home-toothpassport-link" className="tr-link text-[13.5px] font-medium text-slate-900 inline-flex items-center gap-1.5">
                                    Open demo passport <ArrowRight size={13} />
                                </Link>
                                <span className="font-mono-tr text-[10px] tracking-[0.18em] uppercase text-slate-400">Year warranty</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* COMPLIANCE & PATIENT-SAFETY STRIP */}
            <section className="border-t border-slate-200 bg-white" data-testid="home-compliance-strip">
                <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-16">
                    <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
                        <div>
                            <div className="tr-overline mb-3">· Compliance & patient safety</div>
                            <h3 className="font-display font-light text-3xl sm:text-4xl tracking-tight leading-[1.1] text-slate-900 max-w-2xl">
                                Built to the standards that protect your patients — and your practice.
                            </h3>
                            <p className="mt-4 text-[14.5px] text-slate-600 max-w-2xl leading-relaxed">
                                Every case you send us — and every byte of data your patients trust us with — is handled under <strong>Qatar Law No. 13 of 2016 (PDPPL)</strong> and aligned with the international standards below. We don't just promise it. We bake it into every workflow, audit log, and access control.
                            </p>
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {[
                            { code: "ISO/IEC 27001", title: "Information Security", desc: "ISMS controls across access, encryption, and audit." },
                            { code: "ISO/IEC 27701", title: "Privacy Information", desc: "Extends 27001 for PII processors + controllers." },
                            { code: "ISO 13485", title: "Medical Device QMS", desc: "Quality management for the appliances we manufacture." },
                            { code: "GDPR aligned", title: "EU Data Protection", desc: "Adopted as a baseline for cross-border data subjects." },
                            { code: "WHO Patient Safety", title: "Global Action Plan 2021–2030", desc: "Incident-reporting + human-factors mindset." },
                            { code: "ICH-GCP E6(R3)", title: "Good Clinical Practice", desc: "Standard for research-grade clinical data flows." },
                            { code: "PDPPL · Qatar", title: "Law No. 13 of 2016", desc: "Personal data privacy law of the State of Qatar." },
                            { code: "HL7 / FHIR", title: "Clinical Interoperability", desc: "Industry conventions for clinical data exchange." },
                        ].map((b) => (
                            <div key={b.code} data-testid={`compliance-badge-${b.code.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} className="rounded-2xl border border-slate-200 bg-slate-50/40 p-5 hover:border-cyan-300 hover:bg-white transition">
                                <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-cyan-700">{b.code}</div>
                                <div className="mt-2 font-display text-[15.5px] text-slate-900 leading-tight">{b.title}</div>
                                <div className="mt-1.5 text-[12.5px] text-slate-500 leading-relaxed">{b.desc}</div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-8 text-[12.5px] text-slate-500 leading-relaxed max-w-3xl">
                        <span className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-700 mr-1">Note ·</span>
                        Toothmatrix continuously audits its controls against the above standards. Certifications + audit reports are available on request to compliance officers at partner clinics — email <a href="mailto:orders@toothrocket.com" className="text-cyan-700 hover:underline">orders@toothrocket.com</a>.
                    </div>
                </div>
            </section>

            {/* PRICING — usage-based USD */}
            <section
                id="subscriptions"
                data-testid="home-pricing"
                className="relative border-t border-slate-200 bg-gradient-to-br from-slate-50 via-white to-cyan-50/40 overflow-hidden"
            >
                <div className="absolute -top-32 right-10 w-[440px] h-[440px] tr-glow bg-cyan-200/40" aria-hidden />
                <div className="absolute bottom-0 -left-20 w-[360px] h-[360px] tr-glow bg-teal-200/40" aria-hidden />
                <div className="relative max-w-[1280px] mx-auto px-5 lg:px-8 py-24 lg:py-32">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="tr-overline mb-4 flex items-center gap-2">
                                <Sparkles size={13} className="text-cyan-600" /> · Usage-based pricing
                            </div>
                            <h2 className="font-display font-light text-4xl sm:text-5xl tracking-tight leading-[1.05] text-slate-900">
                                Pay for what<br />
                                <span className="italic text-cyan-700">you produce.</span>
                            </h2>
                            <p className="mt-5 text-[15.5px] text-slate-600 leading-[1.75] max-w-xl">
                                Usage-based pricing in USD — pick your monthly case volume and that&rsquo;s your price.
                                Every feature included on every tier, with unlimited users and free practice portals
                                for your clinics.
                            </p>
                            <div className="mt-4 inline-flex items-start gap-2 rounded-xl bg-cyan-50 border border-cyan-100 px-4 py-3 text-[13.5px] text-slate-700 max-w-xl" data-testid="home-pricing-clinics-free">
                                <CheckCircle2 size={15} className="text-cyan-600 mt-0.5 shrink-0" />
                                <span><strong className="text-slate-900">Clinics: free.</strong> No subscriptions, no portal fees — you only pay for the work you order.</span>
                            </div>
                            <ul className="mt-6 space-y-2.5">
                                {[
                                    "No hidden fees — the slider price is the whole price",
                                    "No upfront payments or setup fees",
                                    "3-month money-back guarantee",
                                    "Unlimited licenses included",
                                ].map((t) => (
                                    <li key={t} className="flex items-start gap-2.5 text-[14px] text-slate-700">
                                        <CheckCircle2 size={15} className="text-cyan-600 mt-0.5 shrink-0" /> {t}
                                    </li>
                                ))}
                            </ul>
                            <Link
                                to="/pricing"
                                data-testid="home-pricing-view-all"
                                className="tr-btn-ghost mt-8"
                            >
                                View full pricing &amp; FAQs <ArrowRight size={14} />
                            </Link>
                        </div>
                        <LabPricingSlider />
                    </div>
                </div>
            </section>

            {/* CLOSING CTA */}
            <section className="max-w-[1280px] mx-auto px-5 lg:px-8 py-24" data-testid="home-closing-cta">
                <div className="rounded-3xl border border-slate-200 bg-white p-10 lg:p-16 grid lg:grid-cols-12 gap-8 items-center relative overflow-hidden">
                    <div className="absolute -top-20 -right-20 w-80 h-80 tr-glow bg-cyan-200/60" aria-hidden />
                    <div className="lg:col-span-8 relative">
                        <div className="tr-overline mb-3 flex items-center gap-2">
                            <Beaker size={14} /> Onboarding takes 90 seconds
                        </div>
                        <h3 className="font-display font-light text-4xl sm:text-5xl tracking-tight leading-[1.05] text-slate-900">
                            Become a partner clinic.<br />
                            <span className="italic">Print your first case today.</span>
                        </h3>
                    </div>
                    <div className="lg:col-span-4 flex lg:justify-end gap-3 relative">
                        <Link to="/workflow#send-case" className="tr-btn">Partner With Us <ArrowUpRight size={16} /></Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
